'use client';

import { useId } from 'react';
import { motion } from 'motion/react';
import type { Variants } from 'motion/react';
import { RiPriceTag3Line } from '@remixicon/react';
import { useTranslations } from 'next-intl';

import { Waves } from '@/components/waves';
import { dur, ease, spring, springFade, stagger } from '@/lib/motion';

/**
 * /pricing'in açılışı — koyu blok, ama anasayfanın hero'su DEĞİL.
 *
 * ── Aynı malzeme, başka kurulum ───────────────────────────────────────────
 *
 * Sayfa `.dark-block` ile açılıyor: aynı teal→lacivert rampa, aynı ızgara,
 * header aynı biçimde üst kenara yapışık başlayıp blok bitince beyaz kabuğa
 * geçiyor. Buraya kadarı /features/agent ile ortak.
 *
 * Değişen dört şey — sayfayı anasayfanın kopyası olmaktan çıkaran şey bu
 * dördü, rampa değil:
 *
 *  1. **Işık kaynağı.** Anasayfada tabandan yükselen dalga alanı
 *     (`gradient-waves.tsx`), ajan sayfasında küre (`orb.tsx`), burada ÇİZGİ
 *     ALANI (`waves.tsx`). İnce dikey çizgilerden kurulu, Perlin gürültüsüyle
 *     salınan ve imleç yaklaştığında yay gibi itilip toplanan bir ızgara.
 *     Fiyat sayfasında aranan his tam bu: ölçüm aleti gibi — hassas, sakin,
 *     dokununca cevap veren (DESIGN.md §4).
 *  2. **Hizalama.** Üç hero da ortalanmış; bu sola dayalı. Altındaki üç
 *     bölümün (planlar, planın içi, ince yazı) başlıkları da sola dayalı —
 *     ortalanmış bir açılışın altına sola dayalı üç bölüm gelince sayfa iki
 *     ayrı ızgaradan konuşuyormuş gibi oluyor (aynı gerekçe:
 *     components/page-head.tsx).
 *  3. **Buton yok.** Anasayfa ve ajan hero'su iki butonla bitiyor çünkü
 *     oradaki sonraki adım başka bir sayfada. Burada sonraki adım hemen
 *     ALTTA: plan kartları. Hero'ya "Planları gör" koymak, kullanıcıyı 400px
 *     aşağıdaki şeye tıklamaya davet etmek olurdu.
 *  4. **Künye.** Butonların yerinde satın almadan önce sorulan üç şey var:
 *     kart isteniyor mu, nasıl faturalanıyor, iptal nasıl. Üçü de rakamsız
 *     — kademe ve fiyat kartların işi.
 *
 * Tipografi ödünç: başlık ve açıklama anasayfanın `.new-hero-*` sınıflarını
 * kullanıyor (ajan ve blog hero'ları da öyle). Kopyalanmış bir ölçek değil
 * AYNI ölçek; yalnız hizası bu bölümde sola çevriliyor (globals.css →
 * `.phero .new-hero-title…`). Sınıflar vendor CSS'ine `[data-v-…]`
 * seçicisiyle bağlı, nitelik bu yüzden markup'ta tekrarlanıyor.
 *
 * ── Alan metnin arkasında ama okunurluğu bozmuyor ─────────────────────────
 *
 * Çizgiler %16 beyaz ve katman maskeli: ≥900px'te alan SAĞDA güçlü, metnin
 * durduğu sola doğru sönüyor — kompozisyon sola dayalı metni bilerek boş
 * tarafa oturtuyor. Dar ekranda metin kutunun tamamını kapladığı için maske
 * yön değiştiriyor, alan aşağıda kalıyor.
 *
 * ── Hareket ───────────────────────────────────────────────────────────────
 *
 * Bölüm ekranın tepesinde açılıyor; `whileInView` beklemesi anlamsız,
 * `animate` ile doğrudan oynuyor. Sıra okuma sırası: rozet → başlık → cümle →
 * künye. Blur yalnızca başlıkta.
 *
 * Çizgi alanının kendi döngüsü `<MotionConfig>`in kapsamı dışında; azaltılmış
 * hareket, görünürlük ve piksel bütçesi orada elle karşılanıyor
 * (components/waves.tsx). İmleç takibi `window` üzerinde dinlendiği için
 * katman `pointer-events: none` kalabiliyor — metin seçilebilir duruyor.
 */

/* Vendor CSS'inin kapsam niteliği. Anasayfanın hero sınıfları `[data-v-…]`
   seçicisine bağlı; nitelik olmadan kural hiç uygulanmıyor. */
const V = 'd0b8df6f';

/** Künye sırası: kart → fatura → çıkış. Satın alma tedirginliğinin sırası. */
const FACTS = ['trial', 'billing', 'cancel'] as const;

/* Çizgi alanı: varsayılanlardan daha yavaş ve daha seyrek. Paketin
   varsayılanı (0.0125 / 32px genlik) ekranın tamamını kaplayan bir alanda
   "kıpırdayan" duruyor; bu sayfada alan bir alet yüzeyi gibi durmalı —
   nefes alıyor, dalgalanmıyor (DESIGN.md §4: sakinlik lükstür). */
const FIELD = {
  lineColor: 'hsla(162, 48%, 84%, 0.16)',
  xGap: 14,
  yGap: 34,
  waveAmpX: 24,
  waveAmpY: 10,
  waveSpeedX: 0.008,
  waveSpeedY: 0.004,
  maxCursorMove: 70,
} as const;

const sceneV: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: stagger.loose, delayChildren: 0.05 } },
};

const itemV: Variants = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0, transition: springFade(spring.default) },
};

/* Bölümdeki tek blur: sayfanın adı. */
const titleV: Variants = {
  hidden: { opacity: 0, y: 14, filter: 'blur(6px)' },
  show: {
    opacity: 1,
    y: 0,
    filter: 'blur(0px)',
    transition: springFade(spring.default, dur.base),
  },
};

/* Alan metinden önce değil onunla birlikte açılıyor: ölçek 0.97'den geliyor,
   yani "büyüyerek beliriyor" değil "zaten oradaydı, ışığı açıldı". */
const fieldV: Variants = {
  hidden: { opacity: 0, scale: 0.97 },
  show: { opacity: 1, scale: 1, transition: springFade(spring.soft, dur.slow) },
};

const factsV: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: stagger.tight } },
};

const ruleV: Variants = {
  hidden: { opacity: 0, scaleX: 0 },
  show: { opacity: 1, scaleX: 1, transition: { duration: dur.slow, ease: ease.out } },
};

const factV: Variants = {
  hidden: { opacity: 0, y: 8 },
  show: { opacity: 1, y: 0, transition: springFade(spring.snap) },
};

const edgeV: Variants = {
  hidden: { opacity: 0, scale: 0 },
  show: { opacity: 1, scale: 1, transition: springFade(spring.default) },
};

export function PricingHero() {
  const t = useTranslations('pricing.hero');
  const uid = useId();

  return (
    <section className="phero" aria-labelledby={`${uid}-title`}>
      <div className="phero-body">
        {/* Alan sütunun DIŞINDA, bloğun tamamına yayılı ve onun ALTINDA:
            içerik sütununun dikey hatları çizgilerin üstünde kalıyor. */}
        <motion.div className="phero-field" variants={fieldV} initial="hidden" animate="show">
          <Waves className="phero-field-canvas" {...FIELD} />
        </motion.div>

        {/* Header `position: fixed`, akışta yer kaplamıyor — payı burada. Pay
            bloğun İÇİNDE, kardeşi değil: alan header'ın arkasından da geçiyor
            ve üstteki sönüm şeridi onu orada söndürüyor. */}
        <div className="phero-space" aria-hidden="true" />

        <div className="grid-12-container">
          <div className="grid-item-2fr" />

          <div className="grid-item-8fr">
            <div className="phero-frame">
              <motion.div className="phero-copy" variants={sceneV} initial="hidden" animate="show">
                <motion.p className="phero-eyebrow" variants={itemV}>
                  <RiPriceTag3Line size={18} aria-hidden="true" />
                  {t('eyebrow')}
                </motion.p>

                <motion.h1
                  className="new-hero-title"
                  data-v-d0b8df6f={V}
                  id={`${uid}-title`}
                  variants={titleV}
                >
                  {/* İki satır da span: kırılma noktası viewport genişliğine
                      bağlı kalmıyor, her kademede iki satır. Renk ayrımı
                      globals.css'te — üst satır gri→beyaz, alt satır marka
                      yeşili; vaadi taşıyan yarı alttaki. */}
                  <span
                    className="new-hero-title-line new-hero-title-line--lead"
                    data-v-d0b8df6f={V}
                  >
                    {t('titleLine1')}
                  </span>
                  <span
                    className="new-hero-title-line new-hero-title-line--accent"
                    data-v-d0b8df6f={V}
                  >
                    {t('titleLine2')}
                  </span>
                </motion.h1>

                <motion.p className="new-hero-description" data-v-d0b8df6f={V} variants={itemV}>
                  {t('description')}
                </motion.p>

                {/* Künye `<dl>` çünkü ad–değer çiftleri, dekoratif rozet
                    değil. Ayraçlar hücrenin İÇİNDE: `<dl>`nin doğrudan
                    çocuğu yalnızca `<div>`, `<dt>`, `<dd>` olabilir. */}
                <motion.div className="phero-facts" variants={factsV}>
                  <motion.span className="phero-facts-rule" aria-hidden="true" variants={ruleV} />

                  <dl className="phero-facts-list">
                    {FACTS.map((key, i) => (
                      <motion.div className="phero-fact" key={key} variants={factV}>
                        {/* İlk hücrenin solunda ayraç yok: orada künyeyi açan
                            yatay hat ya da çerçevenin kendi hattı var. */}
                        {i > 0 && (
                          <motion.span
                            className="phero-fact-edge"
                            aria-hidden="true"
                            variants={edgeV}
                          />
                        )}
                        <dt className="phero-fact-label">{t(`facts.${key}.label`)}</dt>
                        <dd className="phero-fact-value">{t(`facts.${key}.value`)}</dd>
                      </motion.div>
                    ))}
                  </dl>
                </motion.div>
              </motion.div>
            </div>
          </div>

          <div className="grid-item-2fr" />
        </div>
      </div>
    </section>
  );
}
